import { z } from 'zod';
import { decimalStringSchema } from './common.js';
import { allergenSchema, dietaryLabelSchema, difficultySchema, recipeOriginSchema, sourceTypeSchema, } from './enums.js';
/**
 * RecipeDraft is the normalized, not-yet-persisted recipe produced by the importer,
 * the AI assistant or OCR. It is what the review screen shows and what the user edits
 * before a real Recipe is created. Versioned so AI prompts/schemas can evolve.
 */
export const RECIPE_DRAFT_SCHEMA_VERSION = '1.0';
export const draftConfidenceSchema = z.enum(['high', 'medium', 'low']);
export const draftIngredientSchema = z.object({
    quantity: decimalStringSchema.nullable().default(null),
    quantityMax: decimalStringSchema.nullable().default(null),
    quantityText: z.string().max(80).nullable().default(null),
    unit: z.string().max(40).nullable().default(null),
    name: z.string().min(1).max(200),
    note: z.string().max(200).nullable().default(null),
    optional: z.boolean().default(false),
    scalable: z.boolean().default(true),
    group: z.string().max(120).nullable().default(null),
    originalText: z.string().max(500).nullable().default(null),
    confidence: draftConfidenceSchema.default('high'),
});
export const draftStepSchema = z.object({
    instruction: z.string().min(1).max(5000),
    durationSeconds: z.number().int().min(0).nullable().default(null),
    group: z.string().max(120).nullable().default(null),
    ingredientNames: z.array(z.string()).default([]),
});
export const draftWarningSchema = z.object({
    code: z.enum([
        'RAW_MEAT',
        'RAW_SEAFOOD',
        'RAW_EGGS',
        'ALLERGEN',
        'UNSAFE_STORAGE',
        'CONFLICTS_WITH_PREFERENCES',
        'ESTIMATED_QUANTITIES',
        'UNVERIFIED_SOURCE',
        'AMBIGUOUS_ADDITION',
        'OTHER',
    ]),
    message: z.string().max(500),
    severity: z.enum(['info', 'warning', 'critical']).default('warning'),
});
export const draftSourceSchema = z.object({
    type: sourceTypeSchema,
    url: z.string().url().nullable().default(null),
    canonicalUrl: z.string().url().nullable().default(null),
    domain: z.string().nullable().default(null),
    siteName: z.string().nullable().default(null),
    author: z.string().nullable().default(null),
    originalTitle: z.string().nullable().default(null),
    attribution: z.string().nullable().default(null),
    fingerprint: z.string().nullable().default(null),
    importerVersion: z.string().nullable().default(null),
    originalImageUrl: z.string().url().nullable().default(null),
    /** Only set when the user explicitly chose to copy the remote image. */
    copiedImageAssetId: z.string().uuid().nullable().default(null),
});
export const recipeDraftSchema = z.object({
    schemaVersion: z.literal(RECIPE_DRAFT_SCHEMA_VERSION).default(RECIPE_DRAFT_SCHEMA_VERSION),
    origin: recipeOriginSchema,
    title: z.string().min(1).max(200),
    description: z.string().max(5000).nullable().default(null),
    servings: decimalStringSchema.nullable().default(null),
    servingsLabel: z.string().max(40).nullable().default(null),
    prepTimeMinutes: z.number().int().min(0).nullable().default(null),
    cookTimeMinutes: z.number().int().min(0).nullable().default(null),
    restTimeMinutes: z.number().int().min(0).nullable().default(null),
    totalTimeMinutes: z.number().int().min(0).nullable().default(null),
    difficulty: difficultySchema.nullable().default(null),
    cuisine: z.string().max(80).nullable().default(null),
    mealType: z.string().max(40).nullable().default(null),
    ingredients: z.array(draftIngredientSchema).max(200),
    steps: z.array(draftStepSchema).max(200),
    tags: z.array(z.string().max(50)).max(50).default([]),
    categories: z.array(z.string().max(80)).max(20).default([]),
    dietaryLabels: z.array(dietaryLabelSchema).default([]),
    allergens: z.array(allergenSchema).default([]),
    notes: z.string().max(10000).nullable().default(null),
    warnings: z.array(draftWarningSchema).default([]),
    /** Details the generator was unsure about or added without explicit input. */
    uncertainties: z.array(z.string().max(300)).default([]),
    /** Questions that would materially improve the draft if answered. */
    followUpQuestions: z.array(z.string().max(300)).max(5).default([]),
    source: draftSourceSchema.nullable().default(null),
    imageUrls: z.array(z.string().url()).default([]),
    language: z.string().max(10).nullable().default(null),
});
/**
 * Strict JSON schema shape handed to LLM providers (structured outputs). Identical to
 * recipeDraftSchema minus defaults so providers can enforce it.
 */
export const aiRecipeDraftOutputSchema = z.object({
    title: z.string(),
    description: z.string().nullable(),
    servings: z.number().positive().nullable(),
    servingsLabel: z.string().nullable(),
    prepTimeMinutes: z.number().int().min(0).nullable(),
    cookTimeMinutes: z.number().int().min(0).nullable(),
    restTimeMinutes: z.number().int().min(0).nullable(),
    difficulty: difficultySchema.nullable(),
    cuisine: z.string().nullable(),
    mealType: z.string().nullable(),
    ingredients: z.array(z.object({
        quantity: z.number().min(0).nullable(),
        quantityMax: z.number().min(0).nullable(),
        quantityText: z.string().nullable(),
        unit: z.string().nullable(),
        name: z.string(),
        note: z.string().nullable(),
        optional: z.boolean(),
        group: z.string().nullable(),
        confidence: draftConfidenceSchema,
    })),
    steps: z.array(z.object({
        instruction: z.string(),
        durationSeconds: z.number().int().min(0).nullable(),
        group: z.string().nullable(),
        ingredientNames: z.array(z.string()),
    })),
    tags: z.array(z.string()),
    dietaryLabels: z.array(dietaryLabelSchema),
    allergens: z.array(allergenSchema),
    warnings: z.array(draftWarningSchema),
    uncertainties: z.array(z.string()),
    followUpQuestions: z.array(z.string()),
    language: z.string().nullable(),
});
//# sourceMappingURL=recipe-draft.js.map